import { useState } from 'react';
import {
    Box,
    Button,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Switch,
    Stack,
    ToggleButtonGroup,
    ToggleButton,
    ListItemText,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Event as EventIcon,
} from '@mui/icons-material';
import {
    useGetEventsQuery,
    useCreateEventMutation,
    useUpdateEventMutation,
    useDeleteEventMutation,
} from '@/store/apiSlice';
import { useCategoriesResources } from '@/store/hooks';
import type { Event, MonthlyRecurrencePattern, YearlyRecurrencePattern } from '@/types';
import { useI18nContext } from '@/i18n/i18n-react.tsx';
import CategoryIcon from '@/components/CategoryIcon';
import { AdminPageHeader } from '@/components/admin/AdminPageHeader.tsx';
import { RefetchButton } from '@/components/admin/RefetchButton.tsx';
import { useAppDialog } from '@/components/dialogs/AppDialog';

const AdminEventsPage = () => {
    const { LL } = useI18nContext();
    const {
        data: events,
        isLoading,
        isFetching: isFetchingEvents,
        refetch: refetchEvents,
    } = useGetEventsQuery();
    const categories = useCategoriesResources();
    const [createEvent] = useCreateEventMutation();
    const [updateEvent] = useUpdateEventMutation();
    const [deleteEvent] = useDeleteEventMutation();
    const { confirm } = useAppDialog();

    const handleRefetch = () => {
        refetchEvents();
    };

    // Define translation-dependent constants inside component
    const WEEKDAYS = [
        { value: 1, label: LL.weekdays.monday() },
        { value: 2, label: LL.weekdays.tuesday() },
        { value: 3, label: LL.weekdays.wednesday() },
        { value: 4, label: LL.weekdays.thursday() },
        { value: 5, label: LL.weekdays.friday() },
        { value: 6, label: LL.weekdays.saturday() },
        { value: 7, label: LL.weekdays.sunday() },
    ];

    const WEEK_POSITIONS = [
        { value: 1, label: LL.weekPositions.first() },
        { value: 2, label: LL.weekPositions.second() },
        { value: 3, label: LL.weekPositions.third() },
        { value: 4, label: LL.weekPositions.fourth() },
        { value: 5, label: LL.weekPositions.last() },
    ];

    const MONTHS = [
        { value: 1, label: LL.months.january() },
        { value: 2, label: LL.months.february() },
        { value: 3, label: LL.months.march() },
        { value: 4, label: LL.months.april() },
        { value: 5, label: LL.months.may() },
        { value: 6, label: LL.months.june() },
        { value: 7, label: LL.months.july() },
        { value: 8, label: LL.months.august() },
        { value: 9, label: LL.months.september() },
        { value: 10, label: LL.months.october() },
        { value: 11, label: LL.months.november() },
        { value: 12, label: LL.months.december() },
    ];

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingEvent, setEditingEvent] = useState<Event | null>(null);
    const [formData, setFormData] = useState({
        name: '',
        location: '',
        startTime: '09:30',
        endTime: '12:00',
        recurrenceType: 'weekly' as 'weekly' | 'monthly' | 'yearly',
        weeklyPattern: [7] as number[],
        monthlyPattern: [{ week: 1, day: 7 }] as MonthlyRecurrencePattern[],
        yearlyPattern: [{ month: 12, day: 25 }] as YearlyRecurrencePattern[],
        active: true,
        excludedCategories: [] as number[],
    });

    const handleOpenDialog = (event?: Event) => {
        if (event) {
            setEditingEvent(event);
            setFormData({
                name: event.name,
                location: event.location,
                startTime: event.startTime.slice(0, 5),
                endTime: event.endTime.slice(0, 5),
                recurrenceType: event.recurrenceType,
                weeklyPattern:
                    event.recurrenceType === 'weekly' ? (event.recurrencePattern as number[]) : [7],
                monthlyPattern:
                    event.recurrenceType === 'monthly'
                        ? (event.recurrencePattern as MonthlyRecurrencePattern[])
                        : [{ week: 1, day: 7 }],
                yearlyPattern:
                    event.recurrenceType === 'yearly'
                        ? (event.recurrencePattern as YearlyRecurrencePattern[])
                        : [{ month: 12, day: 25 }],
                active: event.active,
                excludedCategories: event.excludedCategories || [],
            });
        } else {
            setEditingEvent(null);
            setFormData({
                name: '',
                location: '',
                startTime: '09:30',
                endTime: '12:00',
                recurrenceType: 'weekly',
                weeklyPattern: [7],
                monthlyPattern: [{ week: 1, day: 7 }],
                yearlyPattern: [{ month: 12, day: 25 }],
                active: true,
                excludedCategories: [],
            });
        }
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setEditingEvent(null);
    };

    const handleSave = async () => {
        const recurrencePattern =
            formData.recurrenceType === 'weekly'
                ? formData.weeklyPattern
                : formData.recurrenceType === 'monthly'
                  ? formData.monthlyPattern
                  : formData.yearlyPattern;

        const data = {
            name: formData.name,
            location: formData.location,
            startTime: formData.startTime + ':00',
            endTime: formData.endTime + ':00',
            recurrenceType: formData.recurrenceType,
            recurrencePattern,
            active: formData.active,
            excludedCategories: formData.excludedCategories,
        };

        try {
            if (editingEvent) {
                await updateEvent({ id: editingEvent.id, ...data }).unwrap();
            } else {
                await createEvent(data).unwrap();
            }
            handleCloseDialog();
        } catch (error) {
            console.error('Failed to save event:', error);
        }
    };

    const handleDelete = async (id: number) => {
        if (!(await confirm('Are you sure you want to delete this event?'))) {
            return;
        }
        try {
            await deleteEvent(id).unwrap();
        } catch (error) {
            console.error('Failed to delete event:', error);
        }
    };

    const formatRecurrence = (event: Event) => {
        if (event.recurrenceType === 'weekly') {
            const days = (event.recurrencePattern as number[])
                .map((day) => WEEKDAYS.find((w) => w.value === day)?.label)
                .join(', ');
            return `${LL.adminEventsPage.weeklyPrefix()}: ${days}`;
        } else if (event.recurrenceType === 'monthly') {
            const patterns = (event.recurrencePattern as MonthlyRecurrencePattern[])
                .map((p) => {
                    const week = WEEK_POSITIONS.find((w) => w.value === p.week)?.label;
                    const day = WEEKDAYS.find((w) => w.value === p.day)?.label;
                    return `${week} ${day}`;
                })
                .join(', ');
            return `${LL.adminEventsPage.monthlyPrefix()}: ${patterns}`;
        } else {
            const patterns = (event.recurrencePattern as YearlyRecurrencePattern[])
                .map((p) => {
                    const month = MONTHS.find((m) => m.value === p.month)?.label;
                    return `${month} ${p.day}`;
                })
                .join(', ');
            return `${LL.adminEventsPage.yearlyPrefix()}: ${patterns}`;
        }
    };

    const addMonthlyPattern = () => {
        setFormData({
            ...formData,
            monthlyPattern: [...formData.monthlyPattern, { week: 1, day: 7 }],
        });
    };

    const removeMonthlyPattern = (index: number) => {
        setFormData({
            ...formData,
            monthlyPattern: formData.monthlyPattern.filter((_, i) => i !== index),
        });
    };

    const updateMonthlyPattern = (index: number, field: 'week' | 'day', value: number) => {
        const updated = [...formData.monthlyPattern];
        updated[index] = { ...updated[index], [field]: value };
        setFormData({
            ...formData,
            monthlyPattern: updated,
        });
    };

    const addYearlyPattern = () => {
        setFormData({
            ...formData,
            yearlyPattern: [...formData.yearlyPattern, { month: 1, day: 1 }],
        });
    };

    const removeYearlyPattern = (index: number) => {
        setFormData({
            ...formData,
            yearlyPattern: formData.yearlyPattern.filter((_, i) => i !== index),
        });
    };

    const updateYearlyPattern = (index: number, field: 'month' | 'day', value: number) => {
        const updated = [...formData.yearlyPattern];
        updated[index] = { ...updated[index], [field]: value };
        setFormData({
            ...formData,
            yearlyPattern: updated,
        });
    };

    return (
        <>
            <AdminPageHeader
                icon={<EventIcon />}
                title={LL.adminEventsPage.title()}
                subtitle={LL.adminEventsPage.subtitle?.() || 'Manage recurring events'}
                refetchButton={
                    <RefetchButton onRefetch={handleRefetch} isRefetching={isFetchingEvents} />
                }
                actions={
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenDialog()}
                    >
                        {LL.adminEventsPage.addEvent()}
                    </Button>
                }
            />

            <Box sx={{ px: 3 }}>
                {isLoading ? (
                    <Typography>{LL.common.loading()}</Typography>
                ) : (
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>{LL.adminEventsPage.name()}</TableCell>
                                    <TableCell>{LL.adminEventsPage.location()}</TableCell>
                                    <TableCell>{LL.adminEventsPage.time()}</TableCell>
                                    <TableCell>{LL.adminEventsPage.recurrence()}</TableCell>
                                    <TableCell>{LL.adminEventsPage.excludedCategories()}</TableCell>
                                    <TableCell>{LL.adminEventsPage.status()}</TableCell>
                                    <TableCell align="right">
                                        {LL.adminScheduleTemplatesPage.actions()}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {events?.map((event) => (
                                    <TableRow key={event.id}>
                                        <TableCell>{event.name}</TableCell>
                                        <TableCell>{event.location}</TableCell>
                                        <TableCell>
                                            {event.startTime.slice(0, 5)} -{' '}
                                            {event.endTime.slice(0, 5)}
                                        </TableCell>
                                        <TableCell>{formatRecurrence(event)}</TableCell>
                                        <TableCell>
                                            {event.excludedCategories &&
                                            event.excludedCategories.length > 0 ? (
                                                <Chip
                                                    label={event.excludedCategories.length}
                                                    size="small"
                                                    color="warning"
                                                    variant="filled"
                                                />
                                            ) : (
                                                '-'
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={
                                                    event.active
                                                        ? LL.adminEventsPage.active()
                                                        : LL.adminEventsPage.inactive()
                                                }
                                                size="small"
                                                variant="filled"
                                                color={event.active ? 'success' : 'default'}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton
                                                onClick={() => handleOpenDialog(event)}
                                                size="small"
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton
                                                onClick={() => handleDelete(event.id)}
                                                size="small"
                                                color="error"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                    <DialogTitle>
                        {editingEvent
                            ? LL.adminEventsPage.editEvent()
                            : LL.adminEventsPage.addEvent()}
                    </DialogTitle>
                    <DialogContent>
                        <Stack spacing={3} sx={{ mt: 2 }}>
                            <TextField
                                label={LL.adminEventsPage.eventName()}
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                fullWidth
                                required
                            />

                            <TextField
                                label={LL.adminEventsPage.location()}
                                value={formData.location}
                                onChange={(e) =>
                                    setFormData({ ...formData, location: e.target.value })
                                }
                                fullWidth
                            />

                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <TextField
                                    label={LL.adminEventsPage.startTime()}
                                    type="time"
                                    value={formData.startTime}
                                    onChange={(e) =>
                                        setFormData({ ...formData, startTime: e.target.value })
                                    }
                                    fullWidth
                                    slotProps={{ inputLabel: { shrink: true } }}
                                    required
                                />

                                <TextField
                                    label={LL.adminEventsPage.endTime()}
                                    type="time"
                                    value={formData.endTime}
                                    onChange={(e) =>
                                        setFormData({ ...formData, endTime: e.target.value })
                                    }
                                    fullWidth
                                    slotProps={{ inputLabel: { shrink: true } }}
                                    required
                                />
                            </Box>

                            <FormControl fullWidth>
                                <InputLabel>{LL.adminEventsPage.recurrenceType()}</InputLabel>
                                <Select
                                    value={formData.recurrenceType}
                                    label={LL.adminEventsPage.recurrenceType()}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            recurrenceType: e.target.value as
                                                | 'weekly'
                                                | 'monthly'
                                                | 'yearly',
                                        })
                                    }
                                >
                                    <MenuItem value="weekly">
                                        {LL.adminEventsPage.weekly()}
                                    </MenuItem>
                                    <MenuItem value="monthly">
                                        {LL.adminEventsPage.monthly()}
                                    </MenuItem>
                                    <MenuItem value="yearly">
                                        {LL.adminEventsPage.yearly()}
                                    </MenuItem>
                                </Select>
                            </FormControl>

                            {formData.recurrenceType === 'weekly' && (
                                <Box>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                        {LL.adminEventsPage.selectDaysOfWeek()}
                                    </Typography>
                                    <ToggleButtonGroup
                                        value={formData.weeklyPattern}
                                        onChange={(_, newValue) => {
                                            if (newValue.length > 0) {
                                                setFormData({
                                                    ...formData,
                                                    weeklyPattern: newValue,
                                                });
                                            }
                                        }}
                                        aria-label="weekdays"
                                        sx={{ flexWrap: 'wrap' }}
                                    >
                                        {WEEKDAYS.map((day) => (
                                            <ToggleButton
                                                key={day.value}
                                                value={day.value}
                                                aria-label={day.label}
                                            >
                                                {day.label}
                                            </ToggleButton>
                                        ))}
                                    </ToggleButtonGroup>
                                </Box>
                            )}

                            {formData.recurrenceType === 'monthly' && (
                                <Box>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                        {LL.adminEventsPage.monthlyPatterns()}
                                    </Typography>
                                    {formData.monthlyPattern.map((pattern, index) => (
                                        <Box
                                            key={index}
                                            sx={{
                                                display: 'flex',
                                                gap: 2,
                                                mb: 2,
                                                alignItems: 'center',
                                            }}
                                        >
                                            <FormControl sx={{ flex: 1 }}>
                                                <InputLabel>{LL.adminEventsPage.week()}</InputLabel>
                                                <Select
                                                    value={pattern.week}
                                                    label={LL.adminEventsPage.week()}
                                                    onChange={(e) =>
                                                        updateMonthlyPattern(
                                                            index,
                                                            'week',
                                                            e.target.value as number
                                                        )
                                                    }
                                                >
                                                    {WEEK_POSITIONS.map((week) => (
                                                        <MenuItem
                                                            key={week.value}
                                                            value={week.value}
                                                        >
                                                            {week.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>

                                            <FormControl sx={{ flex: 1 }}>
                                                <InputLabel>{LL.adminEventsPage.day()}</InputLabel>
                                                <Select
                                                    value={pattern.day}
                                                    label={LL.adminEventsPage.day()}
                                                    onChange={(e) =>
                                                        updateMonthlyPattern(
                                                            index,
                                                            'day',
                                                            e.target.value as number
                                                        )
                                                    }
                                                >
                                                    {WEEKDAYS.map((day) => (
                                                        <MenuItem key={day.value} value={day.value}>
                                                            {day.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>

                                            {formData.monthlyPattern.length > 1 && (
                                                <IconButton
                                                    onClick={() => removeMonthlyPattern(index)}
                                                    color="error"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            )}
                                        </Box>
                                    ))}
                                    <Button
                                        startIcon={<AddIcon />}
                                        onClick={addMonthlyPattern}
                                        variant="outlined"
                                        size="small"
                                    >
                                        {LL.adminEventsPage.addPattern()}
                                    </Button>
                                </Box>
                            )}

                            {formData.recurrenceType === 'yearly' && (
                                <Box>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                        {LL.adminEventsPage.yearlyPatterns()}
                                    </Typography>
                                    {formData.yearlyPattern.map((pattern, index) => (
                                        <Box
                                            key={index}
                                            sx={{
                                                display: 'flex',
                                                gap: 2,
                                                mb: 2,
                                                alignItems: 'center',
                                            }}
                                        >
                                            <TextField
                                                label={LL.adminEventsPage.day()}
                                                type="number"
                                                value={pattern.day}
                                                onChange={(e) =>
                                                    updateYearlyPattern(
                                                        index,
                                                        'day',
                                                        parseInt(e.target.value)
                                                    )
                                                }
                                                slotProps={{
                                                    input: {
                                                        inputProps: { min: 1, max: 31 },
                                                    },
                                                }}
                                                sx={{ flex: 1 }}
                                            />

                                            <FormControl sx={{ flex: 1 }}>
                                                <InputLabel>
                                                    {LL.adminEventsPage.month()}
                                                </InputLabel>
                                                <Select
                                                    value={pattern.month}
                                                    label={LL.adminEventsPage.month()}
                                                    onChange={(e) =>
                                                        updateYearlyPattern(
                                                            index,
                                                            'month',
                                                            e.target.value as number
                                                        )
                                                    }
                                                >
                                                    {MONTHS.map((month) => (
                                                        <MenuItem
                                                            key={month.value}
                                                            value={month.value}
                                                        >
                                                            {month.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>

                                            {formData.yearlyPattern.length > 1 && (
                                                <IconButton
                                                    onClick={() => removeYearlyPattern(index)}
                                                    color="error"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            )}
                                        </Box>
                                    ))}
                                    <Button
                                        startIcon={<AddIcon />}
                                        onClick={addYearlyPattern}
                                        variant="outlined"
                                        size="small"
                                    >
                                        {LL.adminEventsPage.addDate()}
                                    </Button>
                                </Box>
                            )}

                            <FormControl fullWidth>
                                <InputLabel>{LL.adminEventsPage.excludedCategories()}</InputLabel>
                                <Select
                                    multiple
                                    value={formData.excludedCategories}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            excludedCategories: e.target.value as number[],
                                        })
                                    }
                                    label={LL.adminEventsPage.excludedCategories()}
                                    renderValue={(selected) => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {selected.length > 0 ? (
                                                selected.map((categoryId) => {
                                                    const category = categories?.find(
                                                        (c) => c.id === categoryId
                                                    );
                                                    if (!category) {
                                                        return null;
                                                    }
                                                    return (
                                                        <Chip
                                                            key={categoryId}
                                                            icon={
                                                                <Box sx={{ ml: 1.5, mb: -1 }}>
                                                                    <CategoryIcon
                                                                        category={category.icon}
                                                                    />
                                                                </Box>
                                                            }
                                                            label={category.name}
                                                            onDelete={(e) => {
                                                                e.stopPropagation();
                                                                setFormData({
                                                                    ...formData,
                                                                    excludedCategories:
                                                                        formData.excludedCategories.filter(
                                                                            (id) =>
                                                                                id !== categoryId
                                                                        ),
                                                                });
                                                            }}
                                                            onMouseDown={(e) => {
                                                                e.stopPropagation();
                                                            }}
                                                        />
                                                    );
                                                })
                                            ) : (
                                                <Typography>
                                                    {LL.adminEventsPage.noExcludedCategories()}
                                                </Typography>
                                            )}
                                        </Box>
                                    )}
                                >
                                    {categories?.map((category) => (
                                        <MenuItem key={category.id} value={category.id}>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                }}
                                            >
                                                <CategoryIcon category={category.icon} />
                                                <ListItemText primary={category.name} />
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.active}
                                        onChange={(e) =>
                                            setFormData({ ...formData, active: e.target.checked })
                                        }
                                    />
                                }
                                label={LL.adminEventsPage.active()}
                            />
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDialog}>{LL.common.cancel()}</Button>
                        <Button onClick={handleSave} variant="contained">
                            {LL.common.save()}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </>
    );
};

export default AdminEventsPage;
